import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import axios from 'axios';
import { API } from '@/lib/api';

const AdminAuthContext = createContext({
  adminUser: null,
  adminToken: null,
  adminLogin: async () => ({ success: false, error: 'Not initialized' }),
  adminLogout: () => {},
  loading: true
});

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [adminUser, setAdminUser] = useState(null);
  const [adminToken, setAdminToken] = useState(() => {
    try {
      return localStorage.getItem('admin_token');
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  const adminLogout = useCallback(() => {
    try {
      localStorage.removeItem('admin_token');
    } catch {
      // Ignore localStorage errors
    }
    setAdminToken(null);
    setAdminUser(null);
  }, []);

  const verifyAdminToken = useCallback(async (token) => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const user = response.data;
      // Check if user has admin access
      if (user.role === 'ADMIN' || user.role === 'STAFF_ADMIN' || user.role === 'admin') {
        setAdminUser(user);
      } else {
        adminLogout();
      }
    } catch (error) {
      console.error('Admin token verification failed:', error);
      adminLogout();
    } finally {
      setLoading(false);
    }
  }, [adminLogout]);

  useEffect(() => {
    verifyAdminToken(adminToken);
  }, [adminToken, verifyAdminToken]);

  const adminLogin = async (email, password) => {
    try {
      const response = await axios.post(`${API}/admin/login`, { email, password });
      const { user, token } = response.data;
      try {
        localStorage.setItem('admin_token', token);
      } catch {
        // Ignore localStorage errors
      }
      setAdminToken(token);
      setAdminUser(user);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  };

  return (
    <AdminAuthContext.Provider value={{ adminUser, adminToken, adminLogin, adminLogout, loading }}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export default AdminAuthContext;
